
import { Box, Divider, Stack, Typography } from "@mui/material";
import SectionTabs from "../SectionTabs";
import CompositionHeader from "../CompositionHeader";
import { PatientCard, PractitionerCard, OrganizationSection, HospitalBanner } from "../InfoCards";
import EncounterSection from "../EncounterSection";
import DocumentReferenceSection from "../DocumentReferenceSection";
import { labelSx, valueSx, formatDate } from "../styles";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const InvoiceView = ({ bundle, setShowPdfPreview }: { bundle: any; setShowPdfPreview: (v: boolean) => void }) => {
    const composition = bundle?.composition || {};
    const patient = bundle || {};
    const invoice = bundle?.invoice || {};
    const invoiceItems = invoice?.invoiceItems || [];

    const sections = [
        {
            id: "invoice",
            label: "Invoice Details",
            content: (
                <Stack spacing={2}>
                    {/* Invoice Header */}
                    <Box
                        sx={{
                            p: 2,
                            border: "2px solid #f0f0f0",
                            borderRadius: 2.5,
                            bgcolor: "linear-gradient(135deg, #fafafa 0%, #f5fffe 100%)",
                            boxShadow: "0 4px 16px rgba(129,196,187,0.12)"
                        }}
                    >
                        <Typography sx={{ ...valueSx, fontWeight: 600, fontSize: "1.1rem", color: "#1f3a2e", mb: 2 }}>
                            Invoice Summary
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                                gap: 2
                            }}
                        >

                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography sx={labelSx}>Invoice Number</Typography>
                                <Typography sx={valueSx}>{invoice.invoiceNumber || "—"}</Typography>
                            </Box>
                            {invoice.transactionId && (
                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                    <Typography sx={labelSx}>Transaction ID</Typography>
                                    <Typography sx={valueSx}>{invoice.transactionId}</Typography>
                                </Box>
                            )}
                            {invoice.totalGross && (
                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                    <Typography sx={labelSx}>Total Amount</Typography>
                                    <Typography sx={{ ...valueSx, fontWeight: 600, color: "#1f3a2e", fontSize: "1.1rem" }}>
                                        {invoice.currency || "INR"} {invoice.totalGross}
                                    </Typography>
                                </Box>
                            )}
                            {invoice.invoiceDate && (
                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                    <Typography sx={labelSx}>Invoice Date</Typography>
                                    <Typography sx={valueSx}>{formatDate(invoice.invoiceDate)}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Invoice Items */}
                    {invoiceItems.length > 0 ? (
                        <Box
                            sx={{
                                p: 2,
                                border: "2px solid #f0f0f0",
                                borderRadius: 2.5,
                                bgcolor: "linear-gradient(135deg, #fafafa 0%, #f5fffe 100%)",
                                boxShadow: "0 4px 16px rgba(129,196,187,0.12)"
                            }}
                        >
                            <Typography sx={{ ...valueSx, fontWeight: 600, fontSize: "1.1rem", color: "#1f3a2e", mb: 2 }}>
                                Invoice Items
                            </Typography>
                            <Stack spacing={1.5}>
                                {invoiceItems.map((item: any, idx: number) => (
                                    <Box
                                        key={`item-${idx}`}
                                        sx={{
                                            p: 2,
                                            border: "1px solid #e0e0e0",
                                            borderRadius: 1.5,
                                            bgcolor: "#fff",
                                            transition: "all 0.3s ease",
                                            "&:hover": {
                                                boxShadow: "0 4px 12px rgba(129,196,187,0.15)",
                                                borderColor: "#2fbf71"
                                            }
                                        }}
                                    >
                                        <Typography sx={{ ...valueSx, fontWeight: 600, mb: 1, color: "#1f3a2e" }}>
                                            {item.description || `Item ${idx + 1}`}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                                                gap: 2
                                            }}
                                        >
                                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                <Typography sx={labelSx}>Quantity</Typography>
                                                <Typography sx={valueSx}>{item.quantity || "—"}</Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                <Typography sx={labelSx}>Unit Price</Typography>
                                                <Typography sx={valueSx}>
                                                    {invoice.currency || "INR"} {item.unitPrice || "—"}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                                <Typography sx={labelSx}>Total Price</Typography>
                                                <Typography sx={{ ...valueSx, fontWeight: 600, color: "#1f3a2e" }}>
                                                    {invoice.currency || "INR"} {item.totalPrice || "—"}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    ) : (
                        <Typography sx={labelSx}>No invoice items found.</Typography>
                    )}
                </Stack>
            )
        },
        {
            id: "attachments",
            label: "Attachments",
            content: bundle?.documentReference || bundle?.binary?.data ? (
                <DocumentReferenceSection
                    documentReference={bundle?.documentReference}
                    binary={bundle?.binary}
                    onPreviewBase64={() => setShowPdfPreview(true)}
                />
            ) : (
                <Typography sx={labelSx}>No attachments available.</Typography>
            )
        }
    ];

    return (
        <Stack spacing={3}>
            <HospitalBanner organization={bundle?.organization} />
            <CompositionHeader composition={composition} />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: "8px !important" }}>
                <PatientCard patient={patient} />
                <PractitionerCard practitioner={patient} />
            </Stack>

            <OrganizationSection organization={bundle?.organization} />
            <EncounterSection encounter={bundle?.encounter} />

            <Divider />
            <SectionTabs sections={sections} initialId="invoice" />
        </Stack>
    );
};

export default InvoiceView;
